import clamp from "lodash/clamp";
import isFunction from "lodash/isFunction";
import { z } from "zod";

import { fromJSON } from "~/tools/from-json";
import { isPresent } from "~/tools/is-present";

import type { SmartHistoryLocation } from "./SmartHistoryLocation";
import { SmartHistoryQueueEntry } from "./SmartHistoryQueueEntry";
import type { SmartHistorySchema } from "./SmartHistorySchema";

export interface SmartHistoryQueueValue {
	url: string;
	isActive: boolean;
}

export interface SmartHistoryQueueOptions {
	/**
	 * The maximum number of entries in the queue.
	 * If the queue exceeds this number, the oldest entries are removed.
	 * If not specified, the queue has no maximum size.
	 */
	readonly maxSize?: number;
}

/**
 * An immutable queue of entries.
 * It uses `SmartHistorySchema` to create entries from urls.
 */
export class SmartHistoryQueue {
	/**
	 * The schema to decode a queue snapshot, so it can be matched against a schema's routes.
	 * A queue converts to a snapshot via `SmartHistoryQueue#encode()`.
	 */
	private static readonly schema = z
		.tuple([
			z.number({ coerce: false }), // should not try to convert the active index to a number from anything else
			z.array(SmartHistoryQueueEntry.schema),
		])
		.transform(([activeIndex, history]) => ({ activeIndex, history }));

	/**
	 * A factory method to create a queue from a JSONed snapshot matching to the schema.
	 */
	public static decode(
		schema: SmartHistorySchema,
		json: string,
		options: SmartHistoryQueueOptions = {},
	): SmartHistoryQueue {
		const [error, result] = fromJSON(json, SmartHistoryQueue.schema);

		if (error != null) {
			return new SmartHistoryQueue(schema, options, [], 0);
		}

		const entries = result.history.map(({ location, keys }) => schema.match(location)?.addKeys(keys)).filter(isPresent);

		return new SmartHistoryQueue(schema, options, entries, result.activeIndex);
	}

	/**
	 * The list of entries in the queue.
	 * Stores the entries in the order of oldest to newest.
	 */
	private readonly entries: ReadonlyArray<SmartHistoryQueueEntry>;

	/**
	 * The index of an active entry in the queue.
	 * It is guaranteed to be in the range of [0, entries.length].
	 * If the queue is empty, it is 0.
	 * If the last enqueued url does not much any `routes` it is entries.length.
	 */
	private readonly activeIndex: number;

	/**
	 * The only way to create a queue is by decoding a snapshot via `SmartHistoryQueue.decode()`.
	 */
	private constructor(
		private readonly schema: SmartHistorySchema,
		private readonly options: SmartHistoryQueueOptions,
		entries: ReadonlyArray<SmartHistoryQueueEntry>,
		activeIndex: number,
	) {
		this.schema = schema;
		this.options = options;
		this.entries = options.maxSize != null ? entries.slice(-options.maxSize) : entries;

		const activeIndexCompensation = entries.length - this.entries.length;
		// it is OK to have an active index that is out of bounds - it indicates that there is no active entry
		this.activeIndex = clamp(activeIndex - activeIndexCompensation, 0, this.entries.length);
	}

	private update(entries: ReadonlyArray<SmartHistoryQueueEntry>, activeIndex: number): SmartHistoryQueue {
		return new SmartHistoryQueue(this.schema, this.options, entries, activeIndex);
	}

	/**
	 * Encodes the minimal information needed to reconstruct the queue.
	 */
	public encode(): unknown {
		return [this.activeIndex, this.entries.map((entry) => entry.encode())];
	}

	public size(): number {
		return this.entries.length;
	}

	/**
	 * Gets an item at the given index
	 *
	 * @returns undefined if the index is out of bounds, otherwise the `SmartHistoryQueueValue`.
	 */
	public at(index: number): undefined | SmartHistoryQueueValue;

	/**
	 * Gets an item at the index provided by the transform function.
	 * It is useful to get an item relative to the current active index, like the previous or the next one.
	 *
	 * @param transform a function that takes the current active index and returns the desired index.
	 *
	 * @returns undefined if the index is out of bounds, otherwise the `SmartHistoryQueueValue`.
	 *
	 * @example
	 *
	 * ```ts
	 * const queue = SmartHistoryQueue.decode(schema, json);
	 * queue.at((activeIndex) => activeIndex - 1); // the previous item
	 * queue.at((activeIndex) => activeIndex + 1); // the next item
	 * ```
	 */
	public at(transform: (activeIndex: number) => number): undefined | SmartHistoryQueueValue;

	// Implementation ^
	public at(indexOrTransform: number | ((activeIndex: number) => number)): undefined | SmartHistoryQueueValue {
		const index = isFunction(indexOrTransform) ? indexOrTransform(this.activeIndex) : indexOrTransform;
		const entry = this.entries[index];

		return entry == null ? undefined : { url: entry.url, isActive: index === this.activeIndex };
	}

	/**
	 * Removes all items and resets the active index to 0.
	 *
	 * @returns a new `SmartHistoryQueue` instance with the same schema and options.
	 */
	public clear(): SmartHistoryQueue {
		return this.update([], 0);
	}

	/**
	 * Changes the active index to the given one.
	 *
	 * @param index the new active index [0, entries.length]
	 *
	 * @returns a new `SmartHistoryQueue` instance with only the active index changed.
	 */
	public jumpTo(index: number): SmartHistoryQueue;

	/**
	 * Changes the active index to the one provided by the transform function.
	 * It is useful when jumping to an index relative to the current active index, like the previous or the next one.
	 *
	 * @param transform a function that takes the current active index and returns the desired index.
	 *
	 * @returns a new `SmartHistoryQueue` instance with only the active index changed.
	 */
	public jumpTo(transform: (activeIndex: number) => number): SmartHistoryQueue;

	// Implementation ^
	public jumpTo(indexOrTransform: number | ((activeIndex: number) => number)): SmartHistoryQueue {
		const nextActiveIndex = isFunction(indexOrTransform) ? indexOrTransform(this.activeIndex) : indexOrTransform;

		if (nextActiveIndex === this.activeIndex || nextActiveIndex < 0 || nextActiveIndex > this.entries.length) {
			return this;
		}

		return this.update(this.entries, nextActiveIndex);
	}

	/**
	 * If the queue has entry with the same key as the given location, it jumps to it.
	 * If the location does not match the schema, none becomes active.
	 * If the location equals to active entry, it updates the active entry.
	 * Otherwise it creates a new entry and makes it active.
	 *
	 * @returns a new `SmartHistoryQueue` instance with updated entries and active index.
	 */
	public enqueue(location: SmartHistoryLocation): SmartHistoryQueue {
		// traveling thru the browser history via back/forward buttons
		const keyIndex = this.entries.findIndex((entry) => entry.hasKey(location.key));
		if (keyIndex >= 0) {
			return this.jumpTo(keyIndex);
		}

		const activeOrLastIndex = clamp(this.activeIndex, 0, this.entries.length - 1);
		const onlyPastAndActiveEntries = this.entries.slice(0, activeOrLastIndex + 1);
		const entry = this.schema.match(location);

		if (entry == null) {
			// no entry is active if the url does not match any route - the active index is out of bounds
			return this.update(onlyPastAndActiveEntries, onlyPastAndActiveEntries.length);
		}

		const activeOrLastEntry = this.entries.at(activeOrLastIndex);
		if (!activeOrLastEntry?.isEqual(entry)) {
			return this.update([...onlyPastAndActiveEntries, entry], onlyPastAndActiveEntries.length);
		}

		// the route either is already active or is the last one in the queue
		// so the activeIndex should be updated
		return this.update(
			this.entries.map((item, index) =>
				index === activeOrLastIndex ? entry.addKeys(activeOrLastEntry.getKeys()) : item,
			),
			activeOrLastIndex,
		);
	}

	/**
	 * Converts the queue to an array of items. The oldest items go first.
	 */
	public toArray<TItem>(predicate: (value: SmartHistoryQueueValue, index: number) => TItem): Array<TItem> {
		return this.entries.map(({ url }, index) => predicate({ url, isActive: index === this.activeIndex }, index));
	}
}
